import { fetchHeadTeachers } from 'containers/SuperAdmin/redux/actions/user';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import {
  addLicense,
  archiveLicense,
  deleteLicense,
  fetchAllLicenses,
  restoreLicense,
  updateLicense,
} from '../../redux/actions/license';
import Grid from '@material-ui/core/Grid';
import { makeStyles } from '@material-ui/core/styles';
import ConfirmationDialog from 'components/DeleteDialog';
import Loader from '../../../../components/Loader';
import PageHeader from '../../components/PageHeader';
import EnhancedTable from '../../components/Table';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import ArchiveIcon from '@material-ui/icons/Archive';
import DeleteIcon from '@material-ui/icons/DeleteForever';
import EditIcon from '@material-ui/icons/Edit';
import RestoreFromTrash from '@material-ui/icons/RestoreFromTrash';
import ChangeLicenseDialog from 'containers/SuperAdmin/components/ChangeLicenseDialog';
import FilterBar from 'containers/SuperAdmin/components/FilterBar';
import RenewLicenseDialog from 'containers/SuperAdmin/components/RenewLicenseDialog';
import { formatDuration } from 'containers/SuperAdmin/constants';
import { enqueueSnackbar } from 'redux/actions';
import LicenseDialog from '../../components/LicenseDialog';

dayjs.extend(utc);

const useStyles = makeStyles(() => ({
  root: {
    flexGrow: 1,
  },
}));

let initialLicense = {
  category: 'Group',
  licenseType: 'School',
  studentLicenses: 650,
  teacherLicenses: 5,
  status: 'Approved',
  email: '',
  isDomainCompliant: true,
  licensee: '',
  validityPeriod: 1,
  validityPeriodUnit: 'Years',
  privateStudentLicenses: 0,
  freeTrial: false,
  teacherAccess: 'full',
  issueDate: null,
};

export default function Licenses({ name }) {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [archiveId, setArchiveId] = useState(null);
  const [archiveType, setArchiveType] = useState(null);
  const [openArchiveDialog, setOpenArchiveDialog] = useState(false);

  const [restoreId, setRestoreId] = useState(null);
  const [openRestoreDialog, setOpenRestoreDialog] = useState(false);

  const [deleteId, setDeleteId] = useState(null);
  const [deleteType, setDeleteType] = useState(null);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);

  const [newAccountDeleteId, setNewAccountDeleteId] = useState(null);
  const [openNewAccountConfirmationDialog, setOpenNewAccountConfirmationDialog] = useState(false);

  const [archiveExistingId, setArchiveExistingId] = useState(null);
  const [openArchiveExistingDialog, setOpenArchiveExistingDialog] = useState(false);

  const [view, setView] = useState('normal');
  const [searchString, setSearchString] = useState('');
  const [licenseData, setLicenseData] = useState(null);
  const [licenseDialog, setLicenseDialog] = useState(false);
  const [changeLicenseDialog, setChangeLicenseDialog] = useState(false);

  const [sort, setSort] = useState({ orderBy: 'issueDate', orderDir: 'desc' });

  const { licenses, fetching, actionLoading } = useSelector(({ admin }) => admin.license);

  const [openWixConversionDialog, setOpenWixConversionDialog] = useState(false);
  const [wixLicenseId, setWixLicenseId] = useState(null);
  const [wixLicenseData, setWixLicenseData] = useState(null);
  const loadPage = (page, sortOptions = {}) => {
    let options = {
      page: 1,
      limit: 1000000,
      ...sortOptions,
    };
    if (view === 'archived') {
      options = {
        ...options,
        isArchived: true,
      };
    }
    if (searchString !== '') {
      options.search = searchString;
    }
    dispatch(fetchAllLicenses(options));
    setSort(sortOptions);
  };

  const standardActions = [
    {
      id: 'change',
      label: 'Change',
      action: (r) => {
        setLicenseData(r);
        setChangeLicenseDialog(true);
      },
      icon: <EditIcon />,
      disabled: () => false,
    },
    {
      id: 'archive',
      label: 'Archive',
      action: (r) => {
        if (r?.isGeneric) {
          dispatch(
            enqueueSnackbar({
              message: 'Cannot archive Generic Student License directly. Please delete it from the linked IT License',
              options: {
                variant: 'error',
              },
            }),
          );
          return;
        }
        if (!r?.activationDate && r?.history?.length === 0 && r?.archiveHistory?.length === 0) {
          setNewAccountDeleteId(r._id);
          setOpenNewAccountConfirmationDialog(true);
        } else if (!r?.activationDate && r?.history?.length > 0 && r?.archiveHistory?.length > 0) {
          setArchiveExistingId(r._id);
          setOpenArchiveExistingDialog(true);
        } else {
          setArchiveId(r._id);
          setArchiveType(r.licenseType);
          setOpenArchiveDialog(true);
        }
      },
      icon: <ArchiveIcon />,
      disabled: (r) => r?.isWixLicense || false,
    },
  ];

  const archiveActions = [
    {
      id: 'restore',
      label: 'Restore',
      action: (r) => {
        if (
          r['archivedTeacherLicenses'] > r['teacherLicenses'] ||
          r['archivedStudentLicenses'] > r['studentLicenses']
        ) {
          dispatch(
            enqueueSnackbar({
              message: 'Cannot restore license with more teachers or students than the original license',
              options: {
                variant: 'error',
              },
            }),
          );
          return;
        }

        if (r?.isWixLicense) {
          setWixLicenseId(r._id);
          setWixLicenseData(r);
          setOpenWixConversionDialog(true);
        } else {
          setRestoreId(r._id);
          setLicenseData(r);
          setOpenRestoreDialog(true);
        }
      },
      icon: <RestoreFromTrash />,
      disabled: () => false,
    },
    {
      id: 'delete',
      label: 'Permanently Delete',
      action: (r) => {
        setDeleteId(r._id);
        setDeleteType(r.licenseType);
        setOpenConfirmationDialog(true);
      },
      icon: <DeleteIcon />,
      disabled: () => false,
    },
  ];

  const headCells = [
    {
      id: 'licensee',
      label: 'Name',
      formatter: (row) => row?.name,
    },
    {
      id: 'licenseType',
      label: 'License',
      formatter: (row) => row['licenseType'][0].toUpperCase() + row['licenseType'].slice(1),
    },
    {
      id: 'teacherLicenses',
      label: 'Teacher Qty',
      formatter: (row) => {
        if (row.isArchived && view === 'archived' && row.licenseType === 'School') {
          return `${row['archivedTeacherLicenses']}/${row['teacherLicenses']}`;
        } else {
          return `${row['consumedTeacherLicenses']}/${row['teacherLicenses']}`;
        }
      },
    },
    {
      id: 'studentLicenses',
      label: 'Student Qty',
      formatter: (row) => {
        if (row.isArchived && view === 'archived' && row.licenseType === 'School') {
          return `${row['archivedStudentLicenses']}/${row['studentLicenses']}`;
        } else if (
          row?.category === 'Individual' &&
          row?.licenseType === 'Teacher' &&
          row['consumedStudentLicenses'] >= 1 &&
          row['studentLicenses'] >= 1
        ) {
          if (row?.isGenericLinked) {
            return `${row['consumedStudentLicenses']}/${row['studentLicenses']} (IGS)`;
          } else return `${row['consumedStudentLicenses']}/${row['studentLicenses']}`;
        } else {
          return `${row['consumedStudentLicenses']}/${row['studentLicenses']}`;
        }
      },
    },
    {
      id: 'validityPeriod',
      label: 'Duration',
      formatter: (r) => formatDuration(r.validityPeriod, r.validityPeriodUnit),
    },
    {
      id: 'activationDate',
      label: 'Activation Date',
      formatter: (r) => {
        const isFuture = dayjs.utc(r.issueDate).startOf('day').isAfter(dayjs.utc().endOf('day'));
        if (r?.status === 'Deactivated') {
          return !r.activationDate && r?.history?.length === 0 && r?.archiveHistory?.length === 0
            ? 'Never Activated'
            : dayjs.utc(r.activationDate).format('MMMM DD, YYYY');
        }
        if (isFuture || ['Pending', 'Approved'].includes(r.status)) {
          return 'Pending Activation';
        }
        return r.activationDate ? dayjs.utc(r.activationDate).format('MMMM DD, YYYY') : r.status;
      },
    },
    {
      id: 'expiryDate',
      label: 'Expiry Date',
      formatter: (r) => {
        if (r.status === 'Deactivated') {
          if (!r.activationDate && r?.history?.length === 0 && r?.archiveHistory?.length === 0) {
            return 'N/A';
          }
          if (r.expiryDate) {
            return dayjs.utc(r.expiryDate).format('MMMM DD, YYYY');
          }
          return 'N/A';
        }
        const isFuture = dayjs.utc(r.issueDate).startOf('day').isAfter(dayjs.utc().endOf('day'));
        if (isFuture || ['Pending', 'Approved'].includes(r.status)) {
          return 'Pending Activation';
        }
        if (r.status === 'Activated') {
          return r.expiryDate ? dayjs.utc(r.expiryDate).format('MMMM DD, YYYY') : 'N/A';
        }
        return r.status;
      },
    },
    {
      id: 'actions',
      label: 'Actions',
      actions: view === 'normal' ? standardActions : archiveActions,
    },
  ];

  useEffect(() => {
    dispatch(fetchAllLicenses({ limit: 1000000, page: 1 }));
    dispatch(fetchHeadTeachers());
  }, [dispatch]);

  return (
    <div className={classes.root}>
      <Loader fetching={fetching || actionLoading} />
      <PageHeader
        onSearch={(search) => {
          setSearchString(search);
          dispatch(fetchAllLicenses({ search, page: 1, limit: 1000000 }));
        }}
        title={name}
        options={
          <>
            <IconButton
              onClick={() => {
                setLicenseData(initialLicense);
                setLicenseDialog(true);
              }}
            >
              <AddIcon />
            </IconButton>
          </>
        }
      />
      <FilterBar
        enableTopics={false}
        searchValue={searchString}
        onSearch={(search) => {
          setSearchString(search);
          let params = {
            page: 1,
            limit: 1000000,
            ...sort,
          };
          if (view === 'archived') {
            params.isArchived = true;
          }
          if (search !== '') {
            params.search = search;
          }
          dispatch(fetchAllLicenses(params));
        }}
        toggle={view === 'archived'}
        onAction={() => {
          setSearchString('');
          let params = {
            page: 1,
            limit: 1000000,
            ...sort,
          };
          if (view === 'normal') {
            params = {
              ...params,
              isArchived: true,
            };
          }
          dispatch(fetchAllLicenses(params));
          setView(view === 'normal' ? 'archived' : 'normal');
        }}
      />
      {licenses && licenses.docs.length > 0 && !fetching && !actionLoading && (
        <Grid container direction="row" justifyContent="flex-start" spacing={2}>
          <Grid item sm={12} xs={12}>
            <EnhancedTable
              data={licenses}
              headCells={headCells}
              handlePage={loadPage}
              orderField={sort.orderBy}
              orderDirection={sort.orderDir}
              handleOrder={(d) => loadPage(licenses.page, d)}
              disablePagination={true}
            />
          </Grid>
        </Grid>
      )}
      {licenses && licenses.docs.length === 0 && !fetching && !actionLoading && (
        <Grid container direction="row" justifyContent="center" spacing={2}>
          <Grid item sm={12} xs={12} style={{ padding: '2rem', textAlign: 'center' }}>
            No results found
          </Grid>
        </Grid>
      )}
      <LicenseDialog
        open={licenseDialog}
        data={initialLicense}
        handleClose={() => {
          setLicenseData(initialLicense);
          setLicenseDialog(false);
        }}
        handleConfirm={(data) => {
          dispatch(addLicense(data));
          setLicenseData(initialLicense);
          setLicenseDialog(false);
        }}
      />
      <ChangeLicenseDialog
        open={changeLicenseDialog}
        data={{
          ...licenseData,
          domain: licenseData?.email?.substring(licenseData?.email?.lastIndexOf('@') + 1),
        }}
        handleClose={() => {
          setChangeLicenseDialog(false);
          dispatch(fetchHeadTeachers());
        }}
        handleConfirm={(data) => {
          dispatch(updateLicense(licenseData?._id, data));
          setChangeLicenseDialog(false);
        }}
      />

      <RenewLicenseDialog
        open={openRestoreDialog}
        data={{
          ...licenseData,
          settings:
            licenseData?.history?.length === 0 ? 'reset' : licenseData?.licenseType === 'School' ? 'preserve' : 'reset',
        }}
        confirmText={'Restore Now'}
        currentDate={true}
        handleClose={() => {
          setOpenRestoreDialog(false);
          setRestoreId(null);
        }}
        handleConfirm={(data) => {
          dispatch(restoreLicense(restoreId, data));
          setRestoreId(null);
          setOpenRestoreDialog(false);
        }}
      />

      <ConfirmationDialog
        open={openWixConversionDialog}
        content={
          "Restoring will convert this from a Wix License to a Native LMS License. Are you sure you'd like to proceed?"
        }
        confirmLabel="Yes"
        cancelLabel="No"
        handleClose={() => setOpenWixConversionDialog(false)}
        handleConfirm={() => {
          setRestoreId(wixLicenseId);
          setLicenseData(wixLicenseData);
          setOpenRestoreDialog(true);
          setOpenWixConversionDialog(false);
        }}
      />

      <ConfirmationDialog
        open={openArchiveDialog}
        content={
          archiveType === 'School'
            ? 'Archiving this active License will cause the License to expire & deactivate. It will also archive the School, Teachers, Classes, & Students belonging to this License. Are you sure you want to archive?'
            : archiveType === 'Teacher'
              ? 'Archiving this active License will delete its classes & cause the License to expire & deactivate. It will also unlink from any active Licenses it was linked to. (The linked Licenses will not be affected & will remain active.) Are you sure you want to archive?'
              : 'Archiving this active License will cause the License to expire & deactivate. It will also unlink from any active Licenses it was linked to. (The linked Licenses will not be affected & will remain active.) Are you sure you want to archive?'
        }
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => {
          setOpenArchiveDialog(false);
          setLicenseData(initialLicense);
        }}
        handleConfirm={() => {
          dispatch(archiveLicense(archiveId));
          setArchiveId(null);
          setOpenArchiveDialog(false);
          setArchiveType(null);
        }}
      />
      <ConfirmationDialog
        open={openConfirmationDialog}
        content={
          deleteType === 'School'
            ? 'Warning! Deleting this License will permanently delete its school & ALL Teachers, Classes, & Students belonging to this License. Are you sure you want to permanently delete? This action can’t be undone!'
            : deleteType === 'Teacher'
              ? 'Warning! Are you sure you want to permanently delete this License? No previously linked Students will be deleted; only this Teacher License & its direct records.'
              : 'Warning! Are you sure you want to permanently delete this License? No previously linked Teacher will be deleted; only this Student License & its direct records.'
        }
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => {
          setOpenConfirmationDialog(false);
          setLicenseData(initialLicense);
          setDeleteId(null);
          setDeleteType(null);
        }}
        handleConfirm={() => {
          dispatch(deleteLicense(deleteId));
          setDeleteId(null);
          setOpenConfirmationDialog(false);
          setDeleteType(null);
        }}
      />
      <ConfirmationDialog
        open={openNewAccountConfirmationDialog}
        title=""
        content={
          'Warning! You are attempting to Archive a License that has not been fully activated. This cannot be archived & will instead be Permanently Deleted. Are you sure you want to Permanently Delete this License?'
        }
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => {
          setOpenNewAccountConfirmationDialog(false);
          setNewAccountDeleteId(null);
        }}
        handleConfirm={() => {
          dispatch(deleteLicense(newAccountDeleteId));
          setNewAccountDeleteId(null);
          setOpenNewAccountConfirmationDialog(false);
        }}
      />
      <ConfirmationDialog
        open={openArchiveExistingDialog}
        title=""
        content={
          'You are attempting to Archive a renewed License that has not been fully reactivated. This License will return to the Archive & revert to its former state prior to being renewed. Are you sure you want to Archive this License?'
        }
        cancelLabel={'No'}
        confirmLabel={'Yes'}
        handleClose={() => {
          setOpenArchiveExistingDialog(false);
          setArchiveExistingId(null);
        }}
        handleConfirm={() => {
          dispatch(archiveLicense(archiveExistingId));
          setOpenArchiveExistingDialog(false);
          setArchiveExistingId(null);
        }}
      />
    </div>
  );
}
